import * as THREE from 'three';
import { Subscription } from 'rxjs';
import { ICharacterAI } from '../../interfaces/ICharacterAI';
import { Character } from '../Character';
import { World } from '../../world/World';
import { PedestrianBehavior } from './PedestrianBehavior';
import { TacticalCombatBehaviour } from './TacticalCombatBehaviour';
import { Idle } from '../states/Idle';
import { LineOfSightService } from '../../world/LineOfSightService';

const EYE_HEIGHT = 1.5;

export class CopBehaviour implements ICharacterAI {
    public character: Character;
    public hasLoS = false;

    private inner: ICharacterAI;
    private wantedLevelSubscription: Subscription | null = null;
    private initialized = false;
    private inCombat = false;

    constructor(
        private readonly world: World,
        private readonly losService: LineOfSightService = new LineOfSightService(),
    ) {}

    public update(timeStep: number): void {
        if (!this.character) { return; }
        if (!this.initialized) { this.init(); }
        this.inner.update(timeStep);
        this.updateHasLoS();
    }

    public destroy(): void {
        this.wantedLevelSubscription?.unsubscribe();
        this.wantedLevelSubscription = null;
        this.inner?.destroy?.();
    }

    private init(): void {
        this.initialized = true;

        if ((this.world.wantedLevelService?.wantedLevel ?? 0) >= 1) {
            this.inCombat = true;
            this.switchTo(new TacticalCombatBehaviour(this.world));
        } else {
            this.switchTo(new PedestrianBehavior());
        }

        this.wantedLevelSubscription = this.world.wantedLevelService?.wantedLevel$.subscribe((level) => {
            if (level >= 1 && !this.inCombat) {
                this.inCombat = true;
                this.switchTo(new TacticalCombatBehaviour(this.world));
            } else if (level === 0 && this.inCombat) {
                this.inCombat = false;
                this.switchTo(new PedestrianBehavior(undefined, this.world.navMesh ?? undefined));
                this.character?.setState(new Idle(this.character));
            }
        });
    }

    private switchTo(behaviour: ICharacterAI): void {
        this.inner?.destroy?.();
        behaviour.character = this.character;
        this.inner = behaviour;
    }

    private updateHasLoS(): void {
        if (!this.inCombat || !this.character || (this.character as any).isDead) {
            this.hasLoS = false;
            return;
        }
        const player = this.world.player;
        if (!player || !this.world.physicsWorld) {
            this.hasLoS = false;
            return;
        }

        const copPos = this.character.getWorldPosition(new THREE.Vector3());
        copPos.y += EYE_HEIGHT;
        const playerPos = player.getWorldPosition(new THREE.Vector3());
        playerPos.y += EYE_HEIGHT;

        this.hasLoS = this.losService.hasLineOfSight(copPos, playerPos, this.world.physicsWorld);
    }
}
